/* dg-spin-wh.js: copy of dg-spin.js for the 49" WHITE (same tooling as the 165 Hz, white finish). Uses the 165 Hz's dgs-49hex-* model unchanged; only the shader re-tones the housing white. dg-spin.js is untouched. */
/* Deco Gear photo-projected turntable (production build of proto/mock/duo/spin.js).
   The fitted 3D shape of the monitor is coloured by projecting the REAL factory renders onto it from the
   exact camera each render was taken from, with per-view visibility (depth maps) and view-dependent blending.
   Requires window.THREE (r128).

   Shopify asset URLs are flat CDN URLs, so every file is passed explicitly:
     DGSpin.mount(canvas, {
       mesh:     '{{ "dgs-49hex-mesh.bin" | asset_url }}',          // packed mesh (.bin; legacy model.json also accepted)
       views:    '{{ "dgs-49hex-views.json" | asset_url }}',        // camera poses
       textures: { front: '{{ "dgs-49hex-front.webp" | asset_url }}', ... },  // keyed by view name (falls back to base + view.img)
       screen:   { screenX: 583, screenY0: 214, screenY1: 555 },    // optional lit-screen overlay rectangle (mm)
       onLanded, preserve, maxDpr, depthRes
     }) -> Promise<{ play(o), setAz(deg), setPower(0..1), paintScreen(fn), screenLoop(fn), stopScreen(), draw(), destroy() }>
   Rendering pauses while the canvas is off-screen (or the tab is hidden) and resumes on return. */
(function () {
  const VS = `
    varying vec3 vW; varying vec3 vN; varying vec2 vUv;
    void main(){ vec4 w = modelMatrix*vec4(position,1.); vW=w.xyz; vN=normalize(mat3(modelMatrix)*normal); vUv=uv;
      gl_Position = projectionMatrix*viewMatrix*w; }`;
  const FS = (n) => `
    #include <packing>
    uniform sampler2D tex[${n}]; uniform sampler2D dep[${n}]; uniform mat4 VP[${n}]; uniform vec3 camP[${n}];
    uniform vec3 eye; uniform float isScreen; uniform float power; uniform sampler2D screenTex; uniform float sigma;
    varying vec3 vW; varying vec3 vN; varying vec2 vUv;
    void main(){
      vec3 N = normalize(vN); vec3 toEye = normalize(eye - vW); if (dot(N,toEye) < 0.) N = -N;
      vec3 eyeDir = normalize(vec3(eye.x, 0., eye.z));
      vec3 acc = vec3(0.); float ws = 0.;
      ${Array.from({ length: n }, (_, i) => `
      { vec4 c = VP[${i}]*vec4(vW,1.);
        if (c.w > 0.) { vec3 nd = c.xyz/c.w; vec2 uv = nd.xy*.5+.5;
          if (uv.x>=0.&&uv.x<=1.&&uv.y>=0.&&uv.y<=1.) {
            float d = nd.z*.5+.5; float sd = unpackRGBAToDepth(texture2D(dep[${i}], uv));
            float vis = clamp((sd - d + 0.0012)/0.0008, 0., 1.);
            vec4 t = texture2D(tex[${i}], uv);
            vec3 toC = normalize(camP[${i}]-vW); float facing = max(dot(N,toC), 0.);
            vec3 cd = normalize(vec3(camP[${i}].x, 0., camP[${i}].z));
            float ang = acos(clamp(dot(cd, eyeDir), -1., 1.));
            float w = vis * t.a * pow(facing, 1.5) * exp(-ang*ang/(sigma*sigma)) + 1e-6*vis*t.a;
            acc += w*t.rgb; ws += w; } } }`).join('')}
      vec3 col;
      if (ws > 1e-5) col = acc/ws;
      else { float l = .10 + .18*max(dot(N, normalize(vec3(-.4,.8,.6))),0.); col = vec3(l*.95, l*.97, l); }
      if (isScreen > 1.5 && power > 0.) {
        vec3 s = texture2D(screenTex, vUv).rgb;
        col = mix(col, s, power);
      }
      // White finish: everything except the active screen keeps the photo's shading, re-toned from black to white.
      bool face = isScreen > .5 && abs(vW.x) < 583. && vW.y > 214. && vW.y < 555.;
      if (!face) { float L = dot(col, vec3(.299, .587, .114)); float k = smoothstep(.02, .55, L); col = vec3(.72 + .28*k) * vec3(.985, .99, 1.);
        // chin badge: the 165's light-on-black logo becomes a dark logo on the white chin
        if (abs(vW.x) < 80. && vW.y > 186. && vW.y < 216.) { float t = smoothstep(.40, .62, L); col = mix(vec3(.826) * vec3(.985, .99, 1.), vec3(.22, .22, .24), t); } }
      gl_FragColor = vec4(col, 1.);
    }`;

  // ---- mesh loading -------------------------------------------------------------------------------------
  // Packed .bin (see proto/mock/duo/pkgtest/pack.js): 'DGM1' | u32 headerLen | JSON header | Float32 pos, [Int8 normals], Uint16/32 idx
  function parseBin(ab) {
    const dv = new DataView(ab);
    if (dv.getUint32(0, false) !== 0x44474d31) throw new Error('DGSpin: not a DGM1 mesh');
    const H = dv.getUint32(4, true);
    const hdr = JSON.parse(new TextDecoder().decode(new Uint8Array(ab, 8, H)));
    const D = 8 + H;
    const parts = hdr.parts.map(p => ({
      name: p.name, screen: !!p.screen,
      pos: new Float32Array(ab, D + p.pos, p.n * 3),
      nrm: p.nrm == null ? null : new Int8Array(ab, D + p.nrm, p.n * 3),
      idx: p.it === 32 ? new Uint32Array(ab, D + p.idx, p.i) : new Uint16Array(ab, D + p.idx, p.i),
    }));
    return { parts, ov: hdr.ov };
  }
  // Legacy model.json ({panel:{v,f}, base:{v,f}, ...}): split the panel's screen faces exactly as spin.js does
  function fromLegacy(mesh) {
    const parts = [];
    for (const [k, p] of Object.entries(mesh)) {
      const V = new Float32Array(p.v), F = p.f;
      if (k !== 'panel') { parts.push({ name: k, pos: V, idx: new Uint32Array(F) }); continue; }
      const sF = [], hF = [];
      for (let i = 0; i < F.length; i += 3) {
        const a = F[i] * 3, b = F[i + 1] * 3, c = F[i + 2] * 3;
        const ux = V[b] - V[a], uy = V[b + 1] - V[a + 1], uz = V[b + 2] - V[a + 2], vx = V[c] - V[a], vy = V[c + 1] - V[a + 1], vz = V[c + 2] - V[a + 2];
        let nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx; const l = Math.hypot(nx, ny, nz) || 1; nz /= l;
        const cz = (V[a + 2] + V[b + 2] + V[c + 2]) / 3;
        (nz > .55 && cz > -60 ? sF : hF).push(F[i], F[i + 1], F[i + 2]);
      }
      parts.push({ name: 'hull', pos: V, idx: new Uint32Array(hF) }, { name: 'screen', screen: true, pos: V, idx: new Uint32Array(sF) });
    }
    // overlay depth profile from the panel's vertices (spin.js)
    const pv = mesh.panel.v, bins = {}, BW = 8;
    for (let i = 0; i < pv.length; i += 3) { const x = pv[i], y = pv[i + 1], z = pv[i + 2]; if (y < 260 || y > 500) continue; const b = Math.round(x / BW); if (bins[b] === undefined || z > bins[b]) bins[b] = z; }
    const keys = Object.keys(bins).map(Number).sort((a, b) => a - b), k0 = keys[0];
    return { parts, ov: { bw: BW, k0, z: Array.from({ length: keys[keys.length - 1] - k0 + 1 }, (_, i) => bins[k0 + i] ?? null) } };
  }
  async function loadMesh(url) {
    const r = await fetch(url); if (!r.ok) throw new Error('DGSpin: mesh ' + r.status);
    if (/\.json(\?|$)/i.test(url)) return fromLegacy(await r.json());
    return parseBin(await r.arrayBuffer());
  }

  async function mount(canvas, opts) {
    opts = Object.assign({}, opts || {});
    const base = opts.base || '';
    const sc = Object.assign({}, opts.screen || {});
    const isPhone = (matchMedia('(pointer: coarse)').matches && Math.min(screen.width, screen.height) < 600) || /iPhone|Android.+Mobile/.test(navigator.userAgent);
    const maxDpr = opts.maxDpr || (isPhone ? 2.5 : 2);

    const [meta, mesh] = await Promise.all([
      fetch(opts.views || base + 'dgs-49hex-views.json').then(r => { if (!r.ok) throw new Error('DGSpin: views ' + r.status); return r.json(); }),
      loadMesh(opts.mesh || base + 'dgs-49hex-mesh.bin'),
    ]);
    const views = meta.views, n = views.length;
    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin('anonymous');
    const texs = await Promise.all(views.map(v => new Promise((res, rej) => loader.load((opts.textures && opts.textures[v.name]) || base + v.img,
      t => { t.minFilter = THREE.LinearMipmapLinearFilter; t.generateMipmaps = true; t.anisotropy = 8; res(t); }, undefined, () => rej(new Error('DGSpin: texture ' + v.name))))));

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, preserveDrawingBuffer: !!opts.preserve });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, maxDpr));

    // geometry: screen faces (front-facing glass) carry a UV over their x/y box for the lit-screen texture
    const scene = new THREE.Scene();
    const screenCanvas = document.createElement('canvas'); screenCanvas.width = 1920; screenCanvas.height = 540;
    const screenTex = new THREE.CanvasTexture(screenCanvas);
    const uniformsBase = () => ({
      tex: { value: texs }, dep: { value: [] }, VP: { value: [] }, camP: { value: views.map(v => new THREE.Vector3().fromArray(v.C)) },
      eye: { value: new THREE.Vector3() }, isScreen: { value: 0 }, power: { value: 0 }, screenTex: { value: screenTex }, sigma: { value: .55 },
    });
    const mats = [], geos = [];
    for (const g of mesh.parts) {
      const bg = new THREE.BufferGeometry(), nv = g.pos.length / 3;
      bg.setAttribute('position', new THREE.BufferAttribute(g.pos, 3));
      const uv = new Float32Array(nv * 2);
      if (g.screen) {
        let x0 = 1e9, x1 = -1e9, y0 = 1e9, y1 = -1e9;
        for (let i = 0; i < g.idx.length; i++) { const q = g.idx[i] * 3; x0 = Math.min(x0, g.pos[q]); x1 = Math.max(x1, g.pos[q]); y0 = Math.min(y0, g.pos[q + 1]); y1 = Math.max(y1, g.pos[q + 1]); }
        for (let i = 0; i < nv; i++) { uv[i * 2] = (g.pos[i * 3] - x0) / (x1 - x0); uv[i * 2 + 1] = (g.pos[i * 3 + 1] - y0) / (y1 - y0); }
      }
      bg.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
      bg.setIndex(new THREE.BufferAttribute(g.idx, 1));
      if (g.nrm && !opts.computeNormals) bg.setAttribute('normal', new THREE.BufferAttribute(g.nrm, 3, true)); else bg.computeVertexNormals();
      const u = uniformsBase(); u.isScreen.value = g.screen ? 1 : 0;
      const m = new THREE.ShaderMaterial({ uniforms: u, vertexShader: VS, fragmentShader: FS(n), side: THREE.DoubleSide });
      mats.push(m); geos.push(bg);
      scene.add(new THREE.Mesh(bg, m));
    }

    // clean curved overlay for the active display area (inset inside the bezel and above the chin)
    const ov = mesh.ov, bins = {}; ov.z.forEach((z, i) => { if (z !== null) bins[ov.k0 + i] = z; });
    const BW = ov.bw, keys = Object.keys(bins).map(Number).sort((a, b) => a - b);
    const zAt = x => { const b = x / BW, i0 = Math.floor(b), i1 = i0 + 1; const z0 = bins[i0] ?? bins[keys[0]], z1 = bins[i1] ?? z0; return z0 + (z1 - z0) * (b - i0); };
    const SX = sc.screenX || 583, SY0 = sc.screenY0 || 214, SY1 = sc.screenY1 || 555, COLS = 96;
    const ovg = new THREE.BufferGeometry(); const P = [], U = [], I = [];
    for (let c = 0; c <= COLS; c++) { const x = -SX + 2 * SX * c / COLS, z = zAt(x) + 1.2; P.push(x, SY0, z, x, SY1, z); U.push(c / COLS, 0, c / COLS, 1); }
    for (let c = 0; c < COLS; c++) { const a = c * 2; I.push(a, a + 2, a + 1, a + 1, a + 2, a + 3); }
    ovg.setAttribute('position', new THREE.Float32BufferAttribute(P, 3)); ovg.setAttribute('uv', new THREE.Float32BufferAttribute(U, 2)); ovg.setIndex(I);
    geos.push(ovg);
    const ovMat = new THREE.MeshBasicMaterial({ map: screenTex, transparent: true, opacity: 0, side: THREE.FrontSide, depthWrite: false, depthTest: false });
    const ovMesh = new THREE.Mesh(ovg, ovMat); ovMesh.renderOrder = 2; ovMesh.visible = false;

    // per-view depth maps from the exact factory cameras
    const VPs = [], deps = [], rts = [];
    const depthMat = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking, side: THREE.DoubleSide });
    const DR = opts.depthRes || 1536;
    for (const v of views) {
      const cam = new THREE.Camera();
      cam.projectionMatrix.fromArray(v.P); cam.projectionMatrixInverse.copy(cam.projectionMatrix).invert();
      cam.matrixWorldInverse.fromArray(v.V); cam.matrixWorld.copy(cam.matrixWorldInverse).invert(); cam.matrixAutoUpdate = false;
      const w = DR, h = Math.round(DR * v.size[1] / v.size[0]);
      const rt = new THREE.WebGLRenderTarget(w, h, { minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter });
      scene.overrideMaterial = depthMat; renderer.setRenderTarget(rt); renderer.setClearColor(0xffffff, 1); renderer.clear(); renderer.render(scene, cam);
      deps.push(rt.texture); rts.push(rt);
      VPs.push(new THREE.Matrix4().fromArray(v.P).multiply(new THREE.Matrix4().fromArray(v.V)));
    }
    scene.overrideMaterial = null; renderer.setRenderTarget(null); renderer.setClearColor(0x000000, 0);
    depthMat.dispose();
    scene.add(ovMesh);
    for (const m of mats) { m.uniforms.dep.value = deps; m.uniforms.VP.value = VPs; }

    // orbit camera (same height and pitch as the factory renders)
    const cam = new THREE.PerspectiveCamera(22, 1, 50, 20000);
    const target = new THREE.Vector3(0, 300, -30);
    let az = 390, el = -2.5;

    // ---- visibility gating: no GPU work while off-screen or in a hidden tab ----
    let onScreen = true, dirty = false, dead = false;
    const visible = () => onScreen && !document.hidden && !dead;
    function size() {
      const r = canvas.getBoundingClientRect(); if (!r.width || !r.height) return;
      renderer.setSize(r.width, r.height, false); cam.aspect = r.width / r.height; cam.updateProjectionMatrix();
      const t = Math.tan(THREE.MathUtils.degToRad(cam.fov / 2));
      cam.userData.dist = Math.max(1420 / (2 * t * cam.aspect), 720 / (2 * t));
    }
    function render() {
      if (!cam.userData.dist) return;
      const a = THREE.MathUtils.degToRad(az), e = THREE.MathUtils.degToRad(el), d = cam.userData.dist;
      cam.position.set(target.x + d * Math.sin(a) * Math.cos(e), target.y + d * Math.sin(e), target.z + d * Math.cos(a) * Math.cos(e));
      cam.lookAt(target);
      for (const m of mats) m.uniforms.eye.value.copy(cam.position);
      renderer.render(scene, cam);
    }
    function draw() { if (visible()) { dirty = false; render(); } else dirty = true; }

    let power = 0;
    function setPower(p) { power = p; for (const m of mats) m.uniforms.power.value = p; ovMat.opacity = p; ovMesh.visible = p > 0.001; }

    // screen content loop: paused (not ticking) while off-screen, resumes on return
    let loop = 0, loopFn = null, loopT0 = 0;
    function tick(now) { loop = 0; if (!loopFn || !visible()) return; loopFn(screenCanvas.getContext('2d'), screenCanvas.width, screenCanvas.height, (now - loopT0) / 1000); screenTex.needsUpdate = true; draw(); loop = requestAnimationFrame(tick); }
    function screenLoop(fn) { cancelAnimationFrame(loop); loopFn = fn; loopT0 = performance.now(); tick(loopT0); }
    function stopScreen() { cancelAnimationFrame(loop); loop = 0; loopFn = null; }
    function paintScreen(fn) { fn(screenCanvas.getContext('2d'), screenCanvas.width, screenCanvas.height); screenTex.needsUpdate = true; }

    function resume() { if (!visible()) return; if (loopFn && !loop) loop = requestAnimationFrame(tick); if (dirty) draw(); }
    let io = null, ro = null;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(es => { for (const e of es) onScreen = e.isIntersecting; resume(); }, { rootMargin: '200px 0px' });
      io.observe(canvas);
    }
    const onVis = () => resume();
    document.addEventListener('visibilitychange', onVis);
    const onResize = () => { size(); draw(); };
    if ('ResizeObserver' in window) { ro = new ResizeObserver(onResize); ro.observe(canvas); } else addEventListener('resize', onResize);
    size(); render();

    const ease = t => t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    let raf = 0;
    function play(o) {
      o = o || {}; cancelAnimationFrame(raf); setPower(0);
      const from = o.from ?? 180, to = o.to ?? 0, dur = o.dur ?? 2300, t0 = performance.now();
      return new Promise(res => {
        (function step(now) {
          const t = Math.min(1, (now - t0) / dur); az = from + (to - from) * ease(t); draw();
          if (t < 1) raf = requestAnimationFrame(step);
          else {
            const p0 = performance.now();
            (function on(now2) { const q = Math.min(1, (now2 - p0) / 700); setPower(q * q * (3 - 2 * q)); draw(); if (q < 1) raf = requestAnimationFrame(on); else { res(); opts.onLanded && opts.onLanded(); } })(p0);
          }
        })(t0);
      });
    }
    // drag to turn
    let drag = null;
    const pd = e => { drag = { x: e.clientX, az }; canvas.setPointerCapture(e.pointerId); };
    const pm = e => { if (!drag) return; az = drag.az - (e.clientX - drag.x) * .35; draw(); };
    const pu = () => { drag = null; };
    canvas.addEventListener('pointerdown', pd); canvas.addEventListener('pointermove', pm); canvas.addEventListener('pointerup', pu); canvas.addEventListener('pointercancel', pu);

    function destroy() {
      dead = true; cancelAnimationFrame(raf); stopScreen();
      io && io.disconnect(); ro ? ro.disconnect() : removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVis);
      canvas.removeEventListener('pointerdown', pd); canvas.removeEventListener('pointermove', pm); canvas.removeEventListener('pointerup', pu); canvas.removeEventListener('pointercancel', pu);
      for (const g of geos) g.dispose(); for (const m of mats) m.dispose(); ovMat.dispose();
      for (const t of texs) t.dispose(); for (const r of rts) r.dispose(); screenTex.dispose(); renderer.dispose();
    }
    return { play, setAz: d => { az = d; draw(); }, setPower: p => { setPower(p); draw(); }, paintScreen, screenLoop, stopScreen, draw, destroy };
  }
  window.DGSpin = { mount };
})();
